import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Storage from 'src/utils/storage/storage';
import { SummaryStripModuleObject } from '../../constants/constant';
import {
  Header,
  ITableData,
  TypeOfTableData
} from '../../pricing-simulator/types/generic-table.model';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';
import {
  fetchPricingSimulationData,
  saveSimulationData,
  resetSimulationData,
  simulateDelistSimulationData
} from '../api/sku-delist-api';
import { updateDropdownSelection } from '../../common/features/table/store/table-slice';
import { calculateGlobalValues } from '../../pricing-simulator/utils/price-action-utils';

const saveDelistSimulationData = createAsyncThunk(
  'saveDelistSimulationData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }) => {
    const response = await saveSimulationData(sourceId, moduleId, scenarioId);
    return response;
  }
);

const resetDelistSimulationData = createAsyncThunk(
  'getSKUDelistData',
  async ({
    sourceId,
    moduleId,
    scenarioId
  }: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  },
    thunkAPI
  ) => {
    const response = await resetSimulationData(sourceId, moduleId, scenarioId);
    const { globalPA1Value } = calculateGlobalValues(
      response.row,
      'delist_implement_after_cal'
    );

    // Dispatch actions to update Redux state
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_1',
        value: globalPA1Value,
      })
    );
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],

      response.header,

      ''
    );

    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );

    return {
      resultCols,
      inputCols,
      response
    };
  }
);

const getSKUDelistData = createAsyncThunk(
  'getSKUDelistData',
  async ({
    dataInput,
    sourceId,
    moduleId,
    scenarioId
  }: {
    dataInput: any;
    sourceId: number;
    moduleId: number;
    scenarioId: number;
  }, thunkAPI) => {
    const isSimulation =
      (dataInput.row && dataInput.row.data && dataInput.row.data.length) ||
      (dataInput.row && dataInput.row.inputs && dataInput.row.inputs.length);
    const response = isSimulation
      ? await simulateDelistSimulationData(
        dataInput,
        sourceId,
        moduleId,
        scenarioId
      )
      : await fetchPricingSimulationData(
        dataInput,
        sourceId,
        moduleId,
        scenarioId
      );

    const { globalPA1Value } = calculateGlobalValues(
      response.row,
      'delist_implement_after_cal'
    );

    // Dispatch actions to update Redux state
    thunkAPI.dispatch(
      updateDropdownSelection({
        columnKey: 'pricing_implement_after_cal_1',
        value: globalPA1Value,
      })
    );
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols,
      isSimulation
    };
  }
);

interface ISkuDelistState {
  fetchingMetaData: boolean;
  fetchingSimulationData: boolean;
  enableReset: boolean;
  enableSimulation: boolean;
  isScenarioSaving: boolean;
  isSimulating: boolean;
  skuDelistedInfo: {
    skuDelisted: number;
    withinMars: number;
    OutsideMars: number;
  };
  inputCols: Header[];
  resultCols: Header[];
  skuTableMetaData: ITableData;
  simResultsViewSetToAbsolute: boolean;
  simulationTableData: any;
  fetchingTableData: boolean;
  simulatedRow?: any;
  isSimulationSaved: boolean;
  isScenarioSavedSuccessfully: boolean;
  savingScenarioData: boolean;
  isSKUEditedClick: boolean;
  getToggleLabelName: string;
  lastSimulatedSnapshot: any | null;
}

const initialState: ISkuDelistState = {
  fetchingMetaData: false,
  isScenarioSavedSuccessfully: false,
  isSKUEditedClick: false,
  fetchingSimulationData: false,
  enableReset: true,
  enableSimulation: true,
  isScenarioSaving: false,
  isSimulating: false,
  skuDelistedInfo: {
    skuDelisted: null,
    withinMars: null,
    OutsideMars: null
  },
  simulationTableData: {},
  simResultsViewSetToAbsolute: true,
  fetchingTableData: true,
  inputCols: [],
  resultCols: [],
  isSimulationSaved: false,
  savingScenarioData: false,
  skuTableMetaData: {
    legend: {
      metadata: []
    },
    ownKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    retailerKPIColumnMetadata: {
      inputCols: [],
      resultCols: []
    },
    metadata: [],
    show_edited: { enabled: false, label: '' },
    row: [],
    toggle: {
      metadata: [],
      data: {}
    },
    output_toggle: {
      metadata: [],
      data: {}
    },
    footer: {
      metadata: {
        enabled: false,
        label: ''
      },
      row: []
    },
    header: [],
    summary_strip: {
      metadata: [],
      data: {
        internal_dt: 0,
        external_dt: 0,
        renovation: SummaryStripModuleObject,
        pricing: SummaryStripModuleObject,
        delist: SummaryStripModuleObject,
        distribution: SummaryStripModuleObject,
        innovation_table: SummaryStripModuleObject
      }
    }
  },
  getToggleLabelName: '',
  simulatedRow: [],
  lastSimulatedSnapshot: null
};

const skuDelistSlice = createSlice({
  name: 'sku-delist',
  initialState,
  reducers: {
    handleDelistSimulationFlagValue(state) {
      state.isSimulating = true;
    },
    handleEditedPPGsForDelist(state) {
      state.isSKUEditedClick = !state.isSKUEditedClick;
    },
    handleDelistDialogCloseEvent(state) {
      state.isScenarioSavedSuccessfully = false;
    },
    handleValueOfDelistSavedScenario(state) {
      state.isScenarioSavedSuccessfully = false;
    },
    resetSimulation(state) {
      state.enableSimulation = false;
      Storage.setSimulationResultFlag(
        { enableSimulation: false },
        'enableDelistSimulation'
      );
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getSKUDelistData.pending, (state) => {
        state.fetchingTableData = true;
      })
      .addCase(getSKUDelistData.fulfilled, (state, action) => {
        state.skuTableMetaData = {
          ...action.payload.response
        };
        state.isSKUEditedClick = false;
        if (action.payload.isSimulation) {
          state.enableSimulation = true;
          state.isSimulating = false;
        }
        state.fetchingTableData = false;
        state.lastSimulatedSnapshot = JSON.parse(
          JSON.stringify(action.payload.response)
        );
      })
      .addCase(getSKUDelistData.rejected, (state) => {
        state.isSimulating = false;
        state.fetchingTableData = false;
      })
      .addCase(saveDelistSimulationData.pending, (state) => {
        state.isSimulationSaved = true;
        state.savingScenarioData = false;
        state.isScenarioSaving = true;
        state.isScenarioSavedSuccessfully = false;
      })
      .addCase(saveDelistSimulationData.fulfilled, (state, action) => {
        state.isSimulationSaved = false;
        state.isScenarioSaving = false;
        state.savingScenarioData = true;
        state.isScenarioSavedSuccessfully = true;
      })
      .addCase(saveDelistSimulationData.rejected, (state) => {
        state.isSimulationSaved = false;
        state.isScenarioSaving = false;
      })
});

export const {
  handleDelistSimulationFlagValue,
  handleValueOfDelistSavedScenario,
  handleDelistDialogCloseEvent,
  handleEditedPPGsForDelist
} = skuDelistSlice.actions;

export {
  getSKUDelistData,
  saveDelistSimulationData,
  resetDelistSimulationData
};

export default skuDelistSlice;
